import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Chip,
  Grid,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Switch,
  FormControlLabel,
  Stack,
  Divider,
  useMediaQuery,
  useTheme,
  Tooltip,
  FormHelperText,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, isValid, parse } from 'date-fns';
import { toast } from 'react-toastify';
import { bookingsAPI, contactsAPI, slotsAPI, subCategoriesAPI } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import BookingForm from '../components/BookingForm';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  EventNote,
  CheckCircle,
  People as PeopleIcon,
  SyncAlt,
  Block,
  Close,
  Person as PersonIcon,
  Notifications as ReminderIcon,
  Videocam as VideocamIcon,
} from '@mui/icons-material';

/** List filter — all values the API may return or accept for filtering */
const statusOptions = ['', 'pending', 'confirmed', 'cancelled', 'completed'];

/** PUT /customer/bookings/status — only these transitions are offered in the UI */
const statusUpdateOptions = ['APPROVE', 'CANCEL', 'COMPLETE', 'NO_SHOW'];

const getStatusMeta = (rawStatus) => {
  const status = String(rawStatus || '').toUpperCase();
  switch (status) {
    case 'PENDING':
    case 'APPROVE':
      return {
        label: status === 'PENDING' ? 'pending' : 'approve',
        chipSx: { bgcolor: 'rgba(255, 193, 7, 0.18)', color: '#9a6a00', fontWeight: 700 },
        optionSx: { color: '#f9a825', fontWeight: 700 },
      };
    case 'CONFIRMED':
      return {
        label: 'confirmed',
        chipSx: { bgcolor: 'rgba(46, 125, 50, 0.12)', color: '#1f7a33', fontWeight: 700 },
        optionSx: { color: '#2e7d32', fontWeight: 700 },
      };
    case 'REJECT':
      return {
        label: 'reject',
        chipSx: { bgcolor: 'rgba(244, 67, 54, 0.12)', color: '#c62828', fontWeight: 700 },
        optionSx: { color: '#c62828', fontWeight: 700 },
      };
    case 'CANCELLED':
    case 'CANCEL':
      return {
        label: status === 'CANCELLED' ? 'cancelled' : 'cancel',
        chipSx: { bgcolor: 'rgba(211, 47, 47, 0.11)', color: '#bf2d2d', fontWeight: 700 },
        optionSx: { color: '#b71c1c', fontWeight: 700 },
      };
    case 'COMPLETED':
    case 'COMPLETE':
      return {
        label: status === 'COMPLETED' ? 'completed' : 'complete',
        chipSx: {
          bgcolor: status === 'COMPLETED' ? 'rgba(37, 99, 235, 0.12)' : 'rgba(76, 175, 80, 0.12)',
          color: status === 'COMPLETED' ? '#2459c5' : '#1f7a33',
          fontWeight: 700,
        },
        optionSx: { color: '#2e7d32', fontWeight: 700 },
      };
    case 'NO_SHOW':
      return {
        label: 'no_show',
        chipSx: { bgcolor: 'rgba(121, 85, 72, 0.11)', color: '#6d4c41', fontWeight: 700 },
        optionSx: { color: '#5d4037', fontWeight: 700 },
      };
    default:
      return {
        label: status || 'Unknown',
        chipSx: { bgcolor: 'rgba(158, 158, 158, 0.12)', color: '#616161' },
        optionSx: { color: '#616161' },
      };
  }
};

/** Popup/list payloads may send ids as `serviceSubCategoryIds: "7,24"` or `[7, 24]`. */
function formatServiceSubCategoryIdsField(raw) {
  if (raw == null || raw === '') return '';
  if (Array.isArray(raw)) {
    return raw
      .map((x) => String(x).trim())
      .filter(Boolean)
      .join(', ');
  }
  return String(raw)
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean)
    .join(', ');
}

function getServiceCategoryIdFromContact(contact) {
  if (!contact || typeof contact !== 'object') return null;
  const id =
    contact.serviceCategoryId ??
    contact.categoryId ??
    contact.contactTypeId ??
    contact.contactCategoryId;
  if (id !== undefined && id !== null && id !== '') return id;
  return null;
}

/** Names already on the payload (no id lookup). */
function joinSubcategoryLabels(labels) {
  const filtered = labels.filter(Boolean);
  return filtered.length > 0 ? filtered.join(', ') : '';
}

function pickNamedStringOrArrayField(d, fieldName) {
  const val = d[fieldName];
  if (Array.isArray(val) && val.length > 0) return val.join(', ');
  if (typeof val === 'string' && String(val).trim()) return String(val).trim();
  return '';
}

function pickSubcategoryNamesFromObjectsArray(arr) {
  if (!Array.isArray(arr) || arr.length === 0 || typeof arr[0] !== 'object') return '';
  return joinSubcategoryLabels(arr.map((row) => pickSubcategoryRowLabel(row)));
}

function pickSubcategoryNamesFromStringsArray(arr) {
  if (!Array.isArray(arr) || arr.length === 0 || typeof arr[0] !== 'string') return '';
  return arr.join(', ');
}

function getRawSubcategoryNameTextFromPayload(d) {
  if (!d || typeof d !== 'object') return '';
  return (
    pickNamedStringOrArrayField(d, 'subCategoryNames') ||
    pickNamedStringOrArrayField(d, 'serviceSubCategoryNames') ||
    pickSubcategoryNamesFromObjectsArray(d.subCategories) ||
    pickSubcategoryNamesFromStringsArray(d.subCategories) ||
    ''
  );
}

/** Primary field from API: `serviceSubCategoryIds` (e.g. `"7,24"` or `[7,24]`). */
function pickServiceSubcategoryIdsRaw(d) {
  if (!d || typeof d !== 'object') return undefined;
  return (
    d.serviceSubCategoryIds ??
    d.service_sub_category_ids ??
    d.ServiceSubCategoryIds
  );
}

/** Prefer `serviceSubCategoryIds`, then `subCategoryIds`, then `subCategoryId`. */
function collectServiceSubcategoryIdsFromPayload(d) {
  if (!d || typeof d !== 'object') return [];
  const parseRaw = (raw) => {
    if (raw == null || raw === '') return [];
    const parts = Array.isArray(raw) ? raw : String(raw).split(',');
    return parts
      .map((p) => Number.parseInt(String(p).trim(), 10))
      .filter((n) => !Number.isNaN(n) && n > 0);
  };
  let ids = parseRaw(pickServiceSubcategoryIdsRaw(d));
  if (ids.length) return [...new Set(ids)];
  ids = parseRaw(d.subCategoryIds);
  if (ids.length) return [...new Set(ids)];
  return [...new Set(parseRaw(d.subCategoryId))];
}

/**
 * Popup responses may nest the booking: `{ booking: { serviceSubCategoryIds: "7,24", ... } }`.
 * Merge so id→name helpers read fields at the top level.
 */
function pickNestedBookingRecord(p, key) {
  const value = p?.[key];
  return typeof value === 'object' && value !== null ? value : null;
}

function mergeBookingViewPayload(p) {
  if (!p || typeof p !== 'object') return p;
  const nested = pickNestedBookingRecord(p, 'booking') ?? pickNestedBookingRecord(p, 'bookingDetails');
  if (nested) {
    return { ...nested, ...p, ...nested };
  }
  return p;
}

function buildViewBookingDataFromPopup(booking, payload) {
  const row = typeof booking === 'object' && booking !== null ? booking : {};
  if (typeof payload === 'object' && payload !== null) {
    return mergeBookingViewPayload({ ...row, ...payload });
  }
  return mergeBookingViewPayload(row);
}

/** Service / appointment address from popup or list payloads. */
function pickServiceAddress(d) {
  if (!d || typeof d !== 'object') return '';
  const guest = typeof d.guest === 'object' && d.guest !== null ? d.guest : null;
  const raw =
    d.serviceAddress ??
    d.service_address ??
    d.ServiceAddress ??
    d.address ??
    d.serviceLocation ??
    d.service_location ??
    guest?.serviceAddress ??
    guest?.service_address ??
    guest?.address;
  if (raw == null) return '';
  return String(raw).trim();
}

/** Popup / list payloads may use camelCase, snake_case, or PascalCase for the booking reference code. */
function pickBookingConfirmationCode(d) {
  if (!d || typeof d !== 'object') return '';
  const raw =
    d.confirmationCode ??
    d.confirmation_code ??
    d.bookingConfirmationCode ??
    d.booking_confirmation_code ??
    d.ConfirmationCode;
  if (raw == null) return '';
  const s = String(raw).trim();
  return s;
}

function parseSubCategoriesListResponse(res) {
  const body = res?.data;
  const list =
    body?.payload ??
    body?.data?.data ??
    body?.data ??
    (Array.isArray(body) ? body : []);
  return Array.isArray(list) ? list : [];
}

function pickSubcategoryRowLabel(s) {
  if (!s || typeof s !== 'object') return '';
  const id = s.id ?? s.subCategoryId;
  return (
    s.name_EN ||
    s.name_DE ||
    s.name ||
    s.title_EN ||
    s.title_DE ||
    s.title ||
    s.label ||
    s.subCategoryName ||
    (id != null ? `#${id}` : '')
  );
}

function extractContactIdFromBookingPayload(d) {
  if (!d || typeof d !== 'object') return null;
  return (
    d.contactId ??
    d.contact_id ??
    d.providerContactId ??
    d.providerContactID ??
    d.contact?.id ?? null
  );
}

function extractServiceCategoryIdFromBookingPayload(d, contactRow) {
  if (!d || typeof d !== 'object') return null;
  const fromContact = contactRow && getServiceCategoryIdFromContact(contactRow);
  if (fromContact != null && fromContact !== '') return fromContact;
  return (
    d.serviceCategoryId ??
    d.service_category_id ??
    d.categoryId ??
    d.category_id ??
    (d.contact != null ? getServiceCategoryIdFromContact(d.contact) : null) ??
    d.serviceCategory?.id ??
    null
  );
}

function bookingHasSubcategoriesForDialog(d) {
  if (!d || typeof d !== 'object') return false;
  if (getRawSubcategoryNameTextFromPayload(d)) return true;
  return collectServiceSubcategoryIdsFromPayload(d).length > 0;
}

/** Raw names first; then `resolvedNames` from API id→label; else comma-separated ids. */
function formatBookingSubcategoriesForDialog(d, resolvedNames = '') {
  const raw = getRawSubcategoryNameTextFromPayload(d);
  if (raw) return raw;
  if (resolvedNames && String(resolvedNames).trim()) return String(resolvedNames).trim();
  const ids = collectServiceSubcategoryIdsFromPayload(d);
  if (ids.length > 0) return ids.join(', ');
  return formatServiceSubCategoryIdsField(pickServiceSubcategoryIdsRaw(d));
}

/** YYYY-MM-DD for table display from API slot / appointment fields */
function formatRowSlotDate(row) {
  const raw = row?.slotDate ?? row?.date ?? row?.appointmentDate;
  if (!raw) return '—';
  const s = String(raw);
  const ymd = s.includes('T') ? s.split('T')[0] : s.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    const [y, mo, d] = ymd.split('-').map(Number);
    const dt = new Date(y, mo - 1, d);
    if (!Number.isNaN(dt.getTime())) {
      return dt.toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
    }
  }
  const d = new Date(raw);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
  }
  return s;
}

function formatRowTimeOnly(row) {
  const st = row?.slotTime ?? row?.startTime ?? row?.appointmentStartTime ?? row?.slotStartTime ?? row?.start_time ?? row?.slot?.slotTime ?? row?.slot?.startTime ?? row?.slot?.start_time;
  const et = row?.slotEndTime ?? row?.endTime ?? row?.appointmentEndTime ?? row?.slotEndTime ?? row?.end_time ?? row?.slot?.slotEndTime ?? row?.slot?.endTime ?? row?.slot?.end_time;

  if (st && et) {
    const s = String(st).trim();
    const e = String(et).trim();
    const sFormatted = s.includes(':') ? s.substring(0, 5) : s;
    const eFormatted = e.includes(':') ? e.substring(0, 5) : e;
    return `${sFormatted}-${eFormatted}`;
  }
  if (st) {
    const s = String(st).trim();
    return s.includes(':') ? s.substring(0, 5) : s;
  }
  return '—';
}

function isActionableBookingStatus(booking) {
  const st = String(booking?.status || '').toUpperCase();
  return st === 'PENDING' || st === 'APPROVE' || st === 'CONFIRMED';
}

function mergeActionableBookingRows(rows, seen, out) {
  (rows || []).forEach((booking) => {
    if (!booking?.id || seen.has(String(booking.id))) return;
    if (!isActionableBookingStatus(booking)) return;
    seen.add(String(booking.id));
    out.push(booking);
  });
}

async function fetchDialogActionableBookings() {
  const baseFilters = { search: '', slotDate: '', contactId: '' };
  const results = await Promise.allSettled([
    bookingsAPI.getBookings({ ...baseFilters, status: 'pending' }, 0, 1000),
    bookingsAPI.getBookings({ ...baseFilters, status: 'confirmed' }, 0, 1000),
  ]);
  const seen = new Set();
  const merged = [];
  for (const result of results) {
    if (result.status !== 'fulfilled') continue;
    const res = result.value;
    if (!res?.data?.success) continue;
    mergeActionableBookingRows(res.data.data, seen, merged);
  }
  return merged;
}

function bookingMatchesSearchQuery(booking, query) {
  const title = (booking.title || '').toLowerCase();
  const customerName = (booking.customerName || '').toLowerCase();
  const firstName = (booking.customerFirstName || '').toLowerCase();
  const lastName = (booking.customerLastName || '').toLowerCase();
  const fullName = `${firstName} ${lastName}`.trim();
  const confCode = pickBookingConfirmationCode(booking).toLowerCase();
  const meetingType = (booking.meetingType || 'offline').toLowerCase();

  return (
    title.includes(query) ||
    customerName.includes(query) ||
    firstName.includes(query) ||
    lastName.includes(query) ||
    fullName.includes(query) ||
    confCode.includes(query) ||
    meetingType.includes(query)
  );
}

function filterBookingsList(bookings, filters) {
  let list = bookings;
  if (filters.contactId !== '' && filters.contactId != null) {
    const cid = String(filters.contactId);
    list = list.filter((booking) => {
      const bid = extractContactIdFromBookingPayload(booking);
      return bid != null && String(bid) === cid;
    });
  }
  const search = filters.search?.trim();
  if (search) {
    const query = search.toLowerCase();
    return list.filter((booking) => bookingMatchesSearchQuery(booking, query));
  }
  return list;
}

function unwrapContactApiResponse(res) {
  const contact =
    res?.data?.data?.data ??
    res?.data?.data ??
    res?.data?.payload ??
    res?.data;
  return typeof contact === 'object' && contact !== null ? contact : null;
}

async function resolveViewBookingSubcategoryLabels(viewBookingData, allContacts) {
  const raw = getRawSubcategoryNameTextFromPayload(viewBookingData);
  if (raw) return raw;

  const ids = collectServiceSubcategoryIdsFromPayload(viewBookingData);
  if (ids.length === 0) return '';

  const contactId = extractContactIdFromBookingPayload(viewBookingData);
  const contactFromList = contactId != null ? allContacts.find((c) => String(c.id) === String(contactId)) : null;
  let catId = extractServiceCategoryIdFromBookingPayload(viewBookingData, contactFromList);

  if (contactId != null && (catId == null || catId === '')) {
    try {
      const res = await contactsAPI.getContact(contactId);
      const fetchedContact = unwrapContactApiResponse(res);
      if (fetchedContact) {
        catId = extractServiceCategoryIdFromBookingPayload(viewBookingData, fetchedContact);
      }
    } catch (error) {
      console.warn('Bookings: failed to load contact for subcategory labels', error);
    }
  }

  if (catId == null || catId === '') return '';

  const res = await subCategoriesAPI.getSubCategories(catId);
  const rows = parseSubCategoriesListResponse(res);
  const byId = new Map();
  rows.forEach((subcategory) => {
    if (!subcategory || typeof subcategory !== 'object') return;
    const id = Number(subcategory.id ?? subcategory.subCategoryId);
    if (Number.isFinite(id) && id > 0) {
      byId.set(id, pickSubcategoryRowLabel(subcategory));
    }
  });
  return ids.map((id) => byId.get(id) ?? String(id)).join(', ');
}

/** Full date + time for “created” style fields */
function formatBookingDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/** Date only (no redundant midnight time when start/end exist separately) */
function formatAppointmentDateOnly(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}

function formatTimeField(value) {
  if (value === undefined || value === null || String(value).trim() === '') return '—';
  const s = String(value).trim();
  if (/^\d{1,2}:\d{2}:\d{2}/.test(s)) return s.slice(0, 5);
  return s;
}

function isCancelledBookingStatus(status) {
  return ['CANCEL', 'CANCELLED'].includes(String(status || '').toUpperCase());
}

function isCompletedBookingStatus(status) {
  return ['COMPLETE', 'COMPLETED'].includes(String(status || '').toUpperCase());
}

function isConfirmedOrApprovedStatus(status) {
  const normalized = String(status || '').toUpperCase();
  return normalized === 'CONFIRMED' || normalized === 'APPROVE';
}

function getActionableBookingsFieldHelperText(fieldError, isLoading, translate) {
  if (fieldError) return fieldError;
  if (isLoading) return translate('pages.bookings.loading');
  return translate('pages.bookings.dialog.noActionableBookings');
}

function getBookingCustomerDisplayName(booking) {
  return [booking?.customerFirstName, booking?.customerLastName].filter(Boolean).join(' ') || booking?.customerName || '—';
}

function resolveCancelDialogBooking(selectedRow, selectedId, actionableBookings) {
  if (selectedRow) return selectedRow;
  if (!selectedId) return null;
  return actionableBookings.find((x) => String(x.id) === String(selectedId)) || null;
}

function CancelBookingDetailsCard({ booking, t }) {
  if (!booking) return null;

  const statusMeta = getStatusMeta(booking.status);
  const meetingTypeLabel = String(booking.meetingType || '').toUpperCase() === 'ONLINE'
    ? t('pages.bookings.meetingType.online')
    : t('pages.bookings.meetingType.offline');

  const detailRows = [
    { label: t('pages.bookings.table.customerName'), value: getBookingCustomerDisplayName(booking) },
    { label: t('pages.bookings.table.title'), value: booking.title || '—' },
    { label: t('pages.bookings.table.bookingDate'), value: formatRowSlotDate(booking) },
    { label: t('pages.bookings.table.time'), value: formatRowTimeOnly(booking) },
    { label: t('pages.bookings.dialog.confirmationCode'), value: pickBookingConfirmationCode(booking) || '—' },
    { label: t('pages.bookings.table.meetingType'), value: meetingTypeLabel },
  ];

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: '#fff5f5',
        border: '1px solid',
        borderColor: 'rgba(211, 47, 47, 0.1)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
        <Box sx={{ p: 1, borderRadius: '50%', bgcolor: 'rgba(211, 47, 47, 0.1)', color: '#d32f2f' }}>
          <PeopleIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a202c' }}>
            {getBookingCustomerDisplayName(booking)}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontWeight: 500 }}>
            {booking.title || '—'}
          </Typography>
        </Box>
        <Chip
          label={statusMeta.label}
          size="small"
          sx={{
            ...statusMeta.chipSx,
            fontWeight: 700,
            fontSize: '0.7rem',
            textTransform: 'uppercase',
            borderRadius: '6px',
          }}
        />
      </Box>
      <Stack spacing={1}>
        {detailRows.map((row) => (
          <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#718096', textTransform: 'uppercase' }}>
              {row.label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a202c', textAlign: 'right' }}>
              {row.value}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

function BookingsTableBody({
  isLoading,
  filteredBookings,
  t,
  onViewBooking,
  onOpenStatusDialog,
  onOpenReminderDialog,
  onEditBooking,
  onOpenCancelDialog,
}) {
  if (isLoading) {
    return (
      <TableRow>
        <TableCell colSpan={9} align="center">
          <Box sx={{ py: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              {t('pages.bookings.loading')}
            </Typography>
          </Box>
        </TableCell>
      </TableRow>
    );
  }

  if (filteredBookings.length === 0) {
    return (
      <TableRow>
        <TableCell colSpan={9} align="center" sx={{ borderBottom: 'none', py: 3 }}>
          <Box sx={{ p: 6, mx: 2, textAlign: 'center', bgcolor: '#fbfbfb', borderRadius: 4, border: '2px dashed #e0e0e0', color: '#888' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              No bookings found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('pages.bookings.emptyHint')}
            </Typography>
          </Box>
        </TableCell>
      </TableRow>
    );
  }

  return filteredBookings.map((row) => (
    <TableRow
      key={row.id}
      hover
      onClick={() => onViewBooking(row)}
      sx={{
        cursor: 'pointer',
        transition: 'background-color 0.2s',
        '&:hover': { bgcolor: 'rgba(255, 140, 66, 0.05) !important' },
      }}
    >
      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600, color: '#1e293b' }}>
        {formatRowSlotDate(row)}
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        {formatRowTimeOnly(row)}
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace', fontWeight: 600, color: '#475569' }}>
        {pickBookingConfirmationCode(row) || '—'}
      </TableCell>
      <TableCell>{row.title}</TableCell>
      <TableCell>{[row.customerFirstName, row.customerLastName].filter(Boolean).join(' ') || row.customerName}</TableCell>
      <TableCell sx={{ textTransform: 'capitalize' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {String(row.meetingType || '').toUpperCase() === 'ONLINE' && row?.meetingUrl ? (
            <Tooltip title={t('pages.bookings.table.joinOnlineMeeting')} arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(row.meetingUrl, '_blank');
                }}
                sx={{
                  mr: 0.5,
                  color: '#FF8C42',
                  '&:hover': { bgcolor: 'rgba(255, 140, 66, 0.1)' },
                }}
              >
                <VideocamIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Box sx={{ mr: 1.5, ml: 0.5, display: 'flex', color: 'text.secondary' }}>
              <PersonIcon sx={{ fontSize: '1.2rem' }} />
            </Box>
          )}
          <Typography variant="body2">
            {String(row.meetingType || '').toUpperCase() === 'ONLINE'
              ? t('pages.bookings.meetingType.online')
              : t('pages.bookings.meetingType.offline')}
          </Typography>
        </Box>
      </TableCell>
      <TableCell sx={{ maxWidth: 100 }}>
        {row?.notes ? (
          <Tooltip title={row.notes} arrow placement="top">
            <Typography
              variant="body2"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontSize: '0.875rem',
              }}
            >
              {row.notes}
            </Typography>
          </Tooltip>
        ) : (
          ''
        )}
      </TableCell>
      <TableCell>
        <Chip
          label={getStatusMeta(row.status).label}
          size="small"
          sx={{
            ...getStatusMeta(row.status).chipSx,
            fontWeight: 700,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            borderRadius: '6px',
          }}
        />
      </TableCell>
      <TableCell align="right">
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
          {isCancelledBookingStatus(row.status) ? (
            <Typography variant="body2" color="text.secondary">-</Typography>
          ) : (
            <>
              <Tooltip title="Update Status">
                <IconButton size="small" aria-label={isActionableBookingStatus(row) ? 'Review pending booking' : 'Update booking status'} sx={{ color: isActionableBookingStatus(row) ? '#d97706' : '#10b981', '&:hover': { color: isActionableBookingStatus(row) ? '#b45309' : '#059669', bgcolor: isActionableBookingStatus(row) ? 'rgba(217, 119, 6, 0.1)' : 'rgba(16, 185, 129, 0.1)' } }} onClick={(e) => { e.stopPropagation(); onOpenStatusDialog(row); }}>
                  <CheckCircle fontSize="small" />
                </IconButton>
              </Tooltip>
              {isConfirmedOrApprovedStatus(row.status) ? (
                <Tooltip title="Send Reminder">
                  <IconButton size="small" sx={{ color: '#6366f1', '&:hover': { color: '#4f46e5', bgcolor: 'rgba(99, 102, 241, 0.1)' } }} onClick={(e) => { e.stopPropagation(); onOpenReminderDialog(row); }}>
                    <ReminderIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
              {!(isCompletedBookingStatus(row.status) || isConfirmedOrApprovedStatus(row.status) || isCancelledBookingStatus(row.status)) && (
                <Tooltip title="Edit Booking">
                  <IconButton size="small" sx={{ color: '#FF8C42', '&:hover': { color: '#e67e3a', bgcolor: 'rgba(255, 140, 66, 0.1)' } }} onClick={(e) => { e.stopPropagation(); onEditBooking(row); }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {!isCompletedBookingStatus(row.status) ? (
                <Tooltip title="Delete/Cancel">
                  <IconButton size="small" sx={{ color: '#ef4444', '&:hover': { color: '#dc2626', bgcolor: 'rgba(239, 68, 68, 0.1)' } }} onClick={(e) => {
                    e.stopPropagation();
                    onOpenCancelDialog(row);
                  }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
            </>
          )}
        </Box>
      </TableCell>
    </TableRow>
  ));
}

const Bookings = () => {
  const { t } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [bookings, setBookings] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    slotDate: '',
    contactId: '',
  });
  const [totalCount, setTotalCount] = useState(0);
  const [openCreate, setOpenCreate] = useState(false);
  const [editBooking, setEditBooking] = useState(null);

  const [allContacts, setAllContacts] = useState([]);
  const [allSlots, setAllSlots] = useState([]);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedCancelBooking, setSelectedCancelBooking] = useState('');
  const [selectedCancelBookingRow, setSelectedCancelBookingRow] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [cancelErrors, setCancelErrors] = useState({ booking: '', reason: '' });
  /** Pending + confirmed (and approve) for Transfer / Provider-unavailable dialogs — not tied to table page or status filter */
  const [dialogActionableBookings, setDialogActionableBookings] = useState([]);
  const [dialogActionableBookingsLoading, setDialogActionableBookingsLoading] = useState(false);

  const closeCancelDialog = () => {
    setCancelDialogOpen(false);
    setCancelErrors({ booking: '', reason: '' });
    setSelectedCancelBooking('');
    setSelectedCancelBookingRow(null);
    setCancelReason('');
    setDialogActionableBookings([]);
  };

  const handleOpenCancelDialog = (booking = null) => {
    setCancelErrors({ booking: '', reason: '' });
    setCancelReason('');
    if (booking) {
      setSelectedCancelBookingRow(booking);
      setSelectedCancelBooking(booking.id || '');
      setCancelDialogOpen(true);
      return;
    }
    setSelectedCancelBookingRow(null);
    setSelectedCancelBooking('');
    setCancelDialogOpen(true);
    loadDialogActionableBookings();
  };

  const loadDialogActionableBookings = useCallback(async () => {
    setDialogActionableBookingsLoading(true);
    setDialogActionableBookings([]);
    try {
      setDialogActionableBookings(await fetchDialogActionableBookings());
    } catch (e) {
      console.error(e);
      setDialogActionableBookings([]);
    } finally {
      setDialogActionableBookingsLoading(false);
    }
  }, []);

  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferData, setTransferData] = useState({ bookingId: '', newContactId: '', newSlotId: '', reason: '', notifyCustomer: true });
  const [transferSubmitting, setTransferSubmitting] = useState(false);
  const [transferErrors, setTransferErrors] = useState({
    bookingId: '',
    newContactId: '',
    newSlotId: '',
    reason: '',
  });
  const [transferDate, setTransferDate] = useState(new Date());
  const [transferSlots, setTransferSlots] = useState([]);
  const [transferSlotsLoading, setTransferSlotsLoading] = useState(false);

  const formatTransferSlotOptionLabel = (s) => {
    if (!s) return '';
    const rawDate = String(s.date || s.slotDate || '').trim();
    const dateStr = rawDate.includes('T') ? rawDate.split('T')[0] : rawDate.slice(0, 10);
    const start = s.startTime || s.start_time || s.fromTime || s.from_time || '';
    const end = s.endTime || s.end_time || s.toTime || s.to_time || '';

    let timeStr = '';
    if (start && end) {
      timeStr = `${String(start).substring(0, 5)} - ${String(end).substring(0, 5)}`;
    } else if (start) {
      timeStr = String(start).substring(0, 5);
    } else if (s.title || s.time || s.slotTime) {
      timeStr = s.title || s.time || s.slotTime;
    }

    const parts = [dateStr, timeStr].filter(Boolean);
    const mainLabel = parts.join(' · ') || 'Available Slot';
    const extraTitle = s.title && s.title !== timeStr && s.title !== mainLabel ? ` (${s.title})` : '';
    return `${mainLabel}${extraTitle}`;
  };

  const isTransferSlotAvailable = (slot) => {
    if (!slot) return false;
    const avail = slot.isAvailable ?? slot.is_available;
    const isAvail = avail === true || avail === 1 || String(avail).toLowerCase() === 'true' || String(avail).toLowerCase() === 'yes';
    const isStatusAvailable = slot.status ? (String(slot.status).toUpperCase() === 'AVAILABLE' || String(slot.status).toUpperCase() === 'OPEN') : true;
    const notBooked = slot.isBooked !== true && String(slot.status || '').toUpperCase() !== 'BOOKED' && String(slot.status || '').toUpperCase() !== 'RESERVED';
    return (isAvail || isStatusAvailable) && notBooked;
  };

  const fetchTransferSlots = useCallback(async (contactId, date) => {
    if (!contactId || !date || !isValid(date)) {
      setTransferSlots([]);
      return;
    }
    setTransferSlotsLoading(true);
    try {
      const targetDateStr = format(date, 'yyyy-MM-dd');
      let res = await slotsAPI.getSlots({
        contactId,
        date: targetDateStr,
      }, 0, 1000);

      let rawData = res?.data?.payload ?? res?.data?.data ?? res?.data ?? [];
      let list = Array.isArray(rawData) ? rawData : (Array.isArray(rawData?.data) ? rawData.data : []);

      if (list.length === 0) {
        const fallbackRes = await slotsAPI.getSlots({ contactId }, 0, 1000);
        const fallbackRaw = fallbackRes?.data?.payload ?? fallbackRes?.data?.data ?? fallbackRes?.data ?? [];
        list = Array.isArray(fallbackRaw) ? fallbackRaw : (Array.isArray(fallbackRaw?.data) ? fallbackRaw.data : []);
      }

      const allSlotsOnDate = list.filter((slot) => {
        if (!slot) return false;
        const rawSlotDate = String(slot.date || slot.slotDate || '').trim();
        const slotDateFormatted = rawSlotDate.includes('T')
          ? rawSlotDate.split('T')[0]
          : rawSlotDate.slice(0, 10);

        return !targetDateStr || !slotDateFormatted || slotDateFormatted === targetDateStr;
      });

      setTransferSlots(allSlotsOnDate);
    } catch (e) {
      console.error('Error fetching transfer slots:', e);
      setTransferSlots([]);
    } finally {
      setTransferSlotsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (transferDialogOpen && transferData.newContactId) {
      fetchTransferSlots(transferData.newContactId, transferDate);
    }
  }, [transferDialogOpen, transferData.newContactId, transferDate, fetchTransferSlots]);

  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [statusData, setStatusData] = useState({ bookingId: '', status: 'APPROVE', notes: '', notifyCustomer: true });
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewBookingData, setViewBookingData] = useState(null);
  /** Id → label from GET subcategories (when payload only has `serviceSubCategoryIds` / ids). */
  const [viewBookingSubcategoryResolvedNames, setViewBookingSubcategoryResolvedNames] = useState('');
  const [viewSubcategoriesNamesLoading, setViewSubcategoriesNamesLoading] = useState(false);

  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [reminderSubmitting, setReminderSubmitting] = useState(false);
  const [reminderData, setReminderData] = useState({ bookingId: '', type: 'email', customMessage: '' });

  useEffect(() => {
    setViewBookingSubcategoryResolvedNames('');
    setViewSubcategoriesNamesLoading(false);
    if (!viewDialogOpen || !viewBookingData) return;

    const raw = getRawSubcategoryNameTextFromPayload(viewBookingData);
    if (raw) return;

    const ids = collectServiceSubcategoryIdsFromPayload(viewBookingData);
    if (ids.length === 0) return;

    let cancelled = false;
    setViewSubcategoriesNamesLoading(true);

    const run = async () => {
      try {
        const labels = await resolveViewBookingSubcategoryLabels(viewBookingData, allContacts);
        if (!cancelled) setViewBookingSubcategoryResolvedNames(labels);
      } catch (error) {
        console.warn('Bookings: failed to resolve subcategory labels', error);
        if (!cancelled) setViewBookingSubcategoryResolvedNames('');
      } finally {
        if (!cancelled) setViewSubcategoriesNamesLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
      setViewSubcategoriesNamesLoading(false);
    };
  }, [viewDialogOpen, viewBookingData, allContacts]);

  useEffect(() => {
    contactsAPI.getContacts('', '', 0, 1000).then(res => {
      if (res?.data?.success) setAllContacts(res.data.data || []);
    }).catch(err => console.error(err));

    slotsAPI.getSlots({}, 0, 1000).then(res => {
      if (res?.data?.success) setAllSlots(res.data.data || []);
    }).catch(err => console.error(err));

  }, []);

  const handleCancelBookingSubmit = async () => {
    const nextErrors = {
      booking: !String(selectedCancelBooking || '').trim()
        ? t('pages.bookings.dialog.cancelErrorBooking')
        : '',
      reason: !String(cancelReason || '').trim()
        ? t('pages.bookings.dialog.cancelErrorReason')
        : '',
    };
    setCancelErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    setCancelSubmitting(true);
    try {
      const response = await bookingsAPI.deleteBooking(selectedCancelBooking, { reason: cancelReason.trim() });
      closeCancelDialog();
      fetchBookings();
      toast.success(response?.data?.message || 'Booking cancelled successfully.', { position: 'top-center' });
    } catch (err) {
      toast.error('Error cancelling booking', { position: 'top-center' });
      console.error(err);
    } finally {
      setCancelSubmitting(false);
    }
  };

  const closeTransferDialog = () => {
    setTransferDialogOpen(false);
    setTransferErrors({ bookingId: '', newContactId: '', newSlotId: '', reason: '' });
    setDialogActionableBookings([]);
  };

  const handleTransferSubmit = async () => {
    const nextErrors = {
      bookingId: !String(transferData.bookingId || '').trim()
        ? t('pages.bookings.dialog.transferErrorBooking')
        : '',
      newContactId: !String(transferData.newContactId || '').trim()
        ? t('pages.bookings.dialog.transferErrorProvider')
        : '',
      newSlotId: !String(transferData.newSlotId || '').trim()
        ? t('pages.bookings.dialog.transferErrorSlot')
        : '',
      reason: !String(transferData.reason || '').trim()
        ? t('pages.bookings.dialog.transferErrorReason')
        : '',
    };
    setTransferErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) {
      return;
    }
    setTransferSubmitting(true);
    try {
      const response = await bookingsAPI.transferBooking(transferData.bookingId, {
        newContactId: String(transferData.newContactId),
        newSlotId: transferData.newSlotId,
        reason: transferData.reason.trim(),
        notes: "Transferred via portal",
        notifyCustomer: transferData.notifyCustomer
      });
      closeTransferDialog();
      setTransferData({ bookingId: '', newContactId: '', newSlotId: '', reason: '', notifyCustomer: true });
      fetchBookings();
      toast.success(response?.data?.message || 'Booking transferred successfully.', { position: 'top-center' });
    } catch (err) {
      toast.error('Error transferring booking', { position: 'top-center' });
      console.error(err);
    } finally {
      setTransferSubmitting(false);
    }
  };

  const handleOpenStatusDialog = (booking) => {
    const normalized = String(booking?.status || '').toUpperCase();
    const initialStatus = statusUpdateOptions.includes(normalized) ? normalized : 'APPROVE';
    setStatusData({
      bookingId: booking?.id || '',
      status: initialStatus,
      notes: '',
      notifyCustomer: true,
    });
    setStatusDialogOpen(true);
  };

  const handleStatusSubmit = async () => {
    if (!statusData.bookingId || !statusData.status) return;
    setStatusSubmitting(true);
    try {
      const response = await bookingsAPI.updateStatus({
        bookingId: String(statusData.bookingId),
        status: String(statusData.status).toLowerCase(),
        notes: statusData.notes ?? '',
        notifyCustomer: Boolean(statusData.notifyCustomer),
      });
      setStatusDialogOpen(false);
      setStatusData({ bookingId: '', status: 'APPROVE', notes: '', notifyCustomer: true });
      fetchBookings();
      toast.success(response?.data?.message || 'Booking status updated successfully.');
    } catch (error) {
      const body = error?.response?.data;
      const msg =
        body?.error?.message ||
        body?.message ||
        'Error updating booking status';
      toast.error(msg);
      console.error(error);
    } finally {
      setStatusSubmitting(false);
    }
  };

  const handleOpenReminderDialog = (booking) => {
    const name = [booking?.customerFirstName, booking?.customerLastName].filter(Boolean).join(' ') || booking?.customerName || 'Customer';
    setReminderData({
      bookingId: booking?.id || '',
      customerName: name,
      bookingTitle: String(booking?.title || '').trim(),
      slotDateDisplay: formatRowSlotDate(booking) + ' ' + formatRowTimeOnly(booking),
      type: 'email',
      customMessage: '',
    });
    setReminderDialogOpen(true);
  };

  const handleReminderSubmit = async () => {
    if (!reminderData.bookingId) return;
    setReminderSubmitting(true);
    try {
      const response = await bookingsAPI.sendReminder(reminderData.bookingId, {
        type: reminderData.type,
        customMessage: reminderData.customMessage,
      });
      setReminderDialogOpen(false);
      toast.success(response?.data?.message || 'Reminder sent successfully.');
    } catch (error) {
      const apiMsg = error?.response?.data?.message || error?.response?.data?.error?.message || 'Error sending reminder';
      toast.error(apiMsg);
      console.error(error);
    } finally {
      setReminderSubmitting(false);
    }
  };

  const fetchBookings = async () => {
    setIsLoading(true);
    try {
      const response = await bookingsAPI.getBookings(
        {
          status: filters.status,
          search: filters.search,
          slotDate: filters.slotDate,
          contactId: filters.contactId,
        },
        page,
        rowsPerPage
      );
      if (response?.data?.success) {
        setBookings(response.data.data || []);
        // Keep dashboard cards stable while switching status cards.
        if (filters.status === '' || filters.status == null) {
          setStatistics(response.data.statistics || null);
        }
        setTotalCount(response.data.pagination?.total || 0);
      }
    } catch (error) {
      console.error('Error loading bookings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewBooking = async (booking) => {
    const bookingId = booking?.id || booking?.bookingId;
    if (!bookingId) return;
    setViewDialogOpen(true);
    setViewLoading(true);
    setViewBookingData(null);
    try {
      const response = await bookingsAPI.getBookingPopup(bookingId);
      const payload =
        response?.data?.data?.data ||
        response?.data?.data ||
        response?.data?.payload ||
        response?.data ||
        null;
      setViewBookingData(buildViewBookingDataFromPopup(booking, payload));
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        'Failed to load booking details'
      );
    } finally {
      setViewLoading(false);
    }
  };

  const detailLabelSx = {
    fontSize: '0.72rem',
    fontWeight: 700,
    color: '#718096', // Modern grey
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    mb: 0.6,
    display: 'block',
  };
  const detailValueSx = {
    fontSize: '0.95rem',
    fontWeight: 500,
    color: '#1a202c', // Darker text
    lineHeight: 1.5,
    wordBreak: 'break-word',
  };

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, filters.status, filters.search, filters.slotDate, filters.contactId]);

  const handleFilterChange = (key, value) => {
    setPage(0);
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearBookingFilters = () => {
    setPage(0);
    setFilters({ status: '', search: '', slotDate: '', contactId: '' });
  };

  const filteredBookings = useMemo(
    () => filterBookingsList(bookings, filters),
    [bookings, filters.search, filters.contactId]
  );

  const slotDatePickerValue = useMemo(() => {
    if (!filters.slotDate) return null;
    const d = parse(filters.slotDate, 'yyyy-MM-dd', new Date());
    return isValid(d) ? d : null;
  }, [filters.slotDate]);

  const bookingDatePickerSlotProps = {
    textField: {
      fullWidth: true,
      color: 'primary',
      InputLabelProps: { shrink: true, color: 'primary' },
      InputProps: {
        sx: { borderRadius: '12px', bgcolor: 'white' },
      },
    },
    field: { clearable: true },
  };

  const handleSlotDatePickerChange = (newValue) => {
    if (newValue == null || !isValid(newValue)) {
      handleFilterChange('slotDate', '');
      return;
    }
    handleFilterChange('slotDate', format(newValue, 'yyyy-MM-dd'));
  };

  return (
    <Box sx={{ width: '100%', p: { xs: 1, sm: 2 }, maxWidth: 1400, pr: { xs: 1, sm: 2 } }}>
      {/* Header section styled like Slots */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 4,
          p: { xs: 2, sm: 4 },
          borderRadius: { xs: 2, sm: 4 },
          background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
          border: '1px solid rgba(255, 140, 66, 0.2)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        }}
      >
        <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 800, letterSpacing: '-0.03em', color: '#262829' }}>
            {t('pages.bookings.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 500 }}>
            {t('pages.bookings.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
          <Button
            variant="outlined"
            fullWidth={isMobile}
            startIcon={<Block />}
            color="error"
            onClick={() => handleOpenCancelDialog()}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              height: '48px',
              px: 3,
              order: 1,
            }}
          >
            {t('pages.bookings.providerUnavailable')}
          </Button>
          <Button
            variant="outlined"
            fullWidth={isMobile}
            startIcon={<SyncAlt />}
            onClick={() => {
              setTransferErrors({ bookingId: '', newContactId: '', newSlotId: '', reason: '' });
              setTransferData({ bookingId: '', newContactId: '', newSlotId: '', reason: '', notifyCustomer: true });
              setTransferDialogOpen(true);
              loadDialogActionableBookings();
            }}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              height: '48px',
              px: 3,
              order: 2,
            }}
          >
            {t('pages.bookings.transfer')}
          </Button>
          <Button
            variant="outlined"
            fullWidth={isMobile}
            startIcon={<RefreshIcon />}
            onClick={fetchBookings}
            disabled={isLoading}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              height: '48px',
              px: 3,
              order: 3,
            }}
          >
            {t('pages.bookings.refresh')}
          </Button>
          <Button
            variant="contained"
            fullWidth={isMobile}
            startIcon={<AddIcon />}
            onClick={() => setOpenCreate(true)}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '1rem',
              background: '#FF8C42',
              boxShadow: '0 4px 15px rgba(255, 140, 66, 0.3)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              order: 4,
              '&:hover': {
                background: '#E06526',
                transform: 'translateY(-2px) scale(1.02)',
                boxShadow: '0 8px 25px rgba(255, 140, 66, 0.4)',
              },
              '&:active': { transform: 'translateY(0)' },
            }}
          >
            {t('pages.bookings.add')}
          </Button>
        </Box>
      </Box>

      {/* Statistics cards like Slots */}
      {statistics && (
        <Grid
          container
          spacing={2}
          sx={{ mb: 4 }}
        >
          <Grid item xs={6} sm={4} md={2.4}>
            <Paper
              elevation={0}
              onClick={() => handleFilterChange('status', '')}
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 4,
                bgcolor: '#f0f4ff',
                border: '1px solid',
                borderColor: filters.status === '' ? '#3f51b5' : 'rgba(63, 81, 181, 0.15)',
                boxShadow: filters.status === '' ? '0 8px 20px rgba(63, 81, 181, 0.12)' : '0 2px 8px rgba(63, 81, 181, 0.06)',
                position: 'relative',
                overflow: 'hidden',
                height: "95%",
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(63, 81, 181, 0.12)' },
              }}
            >
              <Typography variant={isMobile ? 'h5' : 'h3'} sx={{ fontWeight: 800, mb: 1, color: '#3f51b5' }}>
                {statistics.total}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#5c6bc0', display: 'block', lineHeight: 1.2 }}>
                {t('pages.bookings.stats.total')}
              </Typography>
              <EventNote
                sx={{ position: 'absolute', right: -10, bottom: -10, fontSize: { xs: 60, sm: 100 }, opacity: 0.08, color: '#3f51b5' }}
              />
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Paper
              elevation={0}
              onClick={() => handleFilterChange('status', 'pending')}
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 4,
                bgcolor: '#fff8e1',
                border: '1px solid',
                borderColor: filters.status === 'pending' ? '#f9a825' : 'rgba(255, 193, 7, 0.4)',
                boxShadow: filters.status === 'pending' ? '0 8px 20px rgba(249, 168, 37, 0.15)' : '0 2px 8px rgba(255, 193, 7, 0.12)',
                position: 'relative',
                overflow: 'hidden',
                height: "95%",
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(249, 168, 37, 0.15)' },
              }}
            >
              <Typography variant={isMobile ? 'h5' : 'h3'} sx={{ fontWeight: 800, mb: 1, color: '#f9a825' }}>
                {statistics.pending}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#f9a825', display: 'block', lineHeight: 1.2 }}>
                {t('pages.bookings.stats.pending')}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Paper
              elevation={0}
              onClick={() => handleFilterChange('status', 'confirmed')}
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 4,
                bgcolor: '#e8f5e9',
                border: '1px solid',
                borderColor: filters.status === 'confirmed' ? '#2e7d32' : 'rgba(76, 175, 80, 0.2)',
                boxShadow: filters.status === 'confirmed' ? '0 8px 20px rgba(46, 125, 50, 0.12)' : '0 2px 8px rgba(76, 175, 80, 0.06)',
                position: 'relative',
                overflow: 'hidden',
                height: "95%",
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(46, 125, 50, 0.12)' },
              }}
            >
              <Typography variant={isMobile ? 'h5' : 'h3'} sx={{ fontWeight: 800, mb: 1, color: '#2e7d32' }}>
                {statistics.confirmed}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#43a047', display: 'block', lineHeight: 1.2 }}>
                {t('pages.bookings.stats.confirmed')}
              </Typography>
              <CheckCircle
                sx={{ position: 'absolute', right: -10, bottom: -10, fontSize: { xs: 60, sm: 100 }, opacity: 0.08, color: '#2e7d32' }}
              />
            </Paper>
          </Grid>
          <Grid item xs={6} sm={6} md={2.4}>
            <Paper
              elevation={0}
              onClick={() => handleFilterChange('status', 'completed')}
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 4,
                bgcolor: '#e3f2fd',
                border: '1px solid',
                borderColor: filters.status === 'completed' ? '#1565c0' : 'rgba(33, 150, 243, 0.25)',
                boxShadow: filters.status === 'completed' ? '0 8px 20px rgba(21, 101, 192, 0.12)' : '0 2px 8px rgba(33, 150, 243, 0.08)',
                position: 'relative',
                overflow: 'hidden',
                height: "95%",
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(21, 101, 192, 0.12)' },
              }}
            >
              <Typography variant={isMobile ? 'h5' : 'h3'} sx={{ fontWeight: 800, mb: 1, color: '#1565c0' }}>
                {statistics.completed}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#1976d2', display: 'block', lineHeight: 1.2 }}>
                {t('pages.bookings.stats.completed')}
              </Typography>
              <PeopleIcon
                sx={{ position: 'absolute', right: -10, bottom: -10, fontSize: { xs: 60, sm: 100 }, opacity: 0.08, color: '#1565c0' }}
              />
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Paper
              elevation={0}
              onClick={() => handleFilterChange('status', 'cancelled')}
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 4,
                bgcolor: '#ffebee',
                border: '1px solid',
                borderColor: filters.status === 'cancelled' ? '#c62828' : 'rgba(244, 67, 54, 0.2)',
                boxShadow: filters.status === 'cancelled' ? '0 8px 20px rgba(198, 40, 40, 0.12)' : '0 2px 8px rgba(244, 67, 54, 0.06)',
                position: 'relative',
                overflow: 'hidden',
                height: "95%",
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 20px rgba(198, 40, 40, 0.12)' },
              }}
            >
              <Typography variant={isMobile ? 'h5' : 'h3'} sx={{ fontWeight: 800, mb: 1, color: '#c62828' }}>
                {statistics.cancelled}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#e53935', display: 'block', lineHeight: 1.2 }}>
                {t('pages.bookings.stats.cancelled')}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Filters — same pattern as Contacts: search + info icon, primary status select, slot date, clear */}
      <Grid container spacing={2} alignItems="center" sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl
            fullWidth
            color="primary"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
          >
            <InputLabel id="bookings-contact-filter-label">Select Service</InputLabel>
            <Select
              labelId="bookings-contact-filter-label"
              label="Select Service"
              value={filters.contactId === '' || filters.contactId == null ? '' : String(filters.contactId)}
              onChange={e => handleFilterChange('contactId', e.target.value)}
            >
              <MenuItem value="">{t('pages.bookings.filter.allContacts')}</MenuItem>
              {allContacts.map(c => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.title || c.contactPerson || c.name || `#${c.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            color="primary"
            placeholder={t('pages.bookings.filter.searchTitleCustomerPlaceholder')}
            label={t('pages.bookings.filter.searchTitleCustomer')}
            value={filters.search}
            onChange={e => handleFilterChange('search', e.target.value)}
            InputProps={{
              sx: { borderRadius: '12px', bgcolor: 'white' },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FormControl
            fullWidth
            color="primary"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}
          >
            <InputLabel id="bookings-status-filter-label">{t('pages.bookings.filter.bookingStatus')}</InputLabel>
            <Select
              labelId="bookings-status-filter-label"
              label={t('pages.bookings.filter.bookingStatus')}
              value={filters.status}
              onChange={e => handleFilterChange('status', e.target.value)}
              renderValue={(selected) => {
                if (selected === '' || selected == null) {
                  return (
                    <Typography component="span" variant="body2" sx={{ color: 'text.primary', fontStyle: 'italic' }}>
                      {t('pages.bookings.filter.allStatuses')}
                    </Typography>
                  );
                }
                return (
                  <Typography component="span" variant="body2" sx={{ color: 'text.primary' }}>
                    {getStatusMeta(selected).label}
                  </Typography>
                );
              }}
            >
              <MenuItem value="" sx={{ color: 'text.primary', fontStyle: 'italic' }}>
                {t('pages.bookings.filter.allStatuses')}
              </MenuItem>
              {statusOptions.filter(Boolean).map(opt => (
                <MenuItem key={opt} value={opt} sx={{ color: 'text.primary' }}>
                  {getStatusMeta(opt).label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <DatePicker
            format="dd/MM/yyyy"
            label={t('pages.bookings.filter.slotDate')}
            value={slotDatePickerValue}
            onChange={handleSlotDatePickerChange}
            views={['year', 'month', 'day']}
            openTo="day"
            slotProps={bookingDatePickerSlotProps}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={2}>
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            startIcon={<Close />}
            onClick={clearBookingFilters}
            sx={{
              height: 56,
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              borderWidth: 2,
              '&:hover': { borderWidth: 2 },
            }}
          >
            {t('pages.bookings.clearFilters')}
          </Button>
        </Grid>
      </Grid>

      {/* Table */}
      <Paper elevation={0} sx={{
        borderRadius: 4,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
      }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700, color: '#475569', py: 2 }}>{t('pages.bookings.table.bookingDate')}</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569', py: 2 }}>{t('pages.bookings.table.time')}</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569', py: 2 }}>{t('pages.bookings.dialog.confirmationCode')}</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569', py: 2 }}>{t('pages.bookings.table.title')}</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569', py: 2 }}>{t('pages.bookings.table.customerName')}</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569', py: 2 }}>{t('pages.bookings.table.meetingType')}</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569', py: 2 }}>{t('pages.bookings.table.notes')}</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#475569', py: 2 }}>{t('pages.bookings.table.status')}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#475569', py: 2 }}>{t('pages.bookings.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <BookingsTableBody
                isLoading={isLoading}
                filteredBookings={filteredBookings}
                t={t}
                onViewBooking={handleViewBooking}
                onOpenStatusDialog={handleOpenStatusDialog}
                onOpenReminderDialog={handleOpenReminderDialog}
                onEditBooking={setEditBooking}
                onOpenCancelDialog={handleOpenCancelDialog}
              />
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => {
            setRowsPerPage(Number.parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>

      <Dialog
        open={cancelDialogOpen}
        onClose={closeCancelDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          elevation: 8,
          sx: {
            borderRadius: isMobile ? 0 : 3,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 2.5,
            background: 'linear-gradient(135deg, #fff5f5 0%, #ffffff 100%)',
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: 'rgba(211, 47, 47, 0.1)', color: '#d32f2f' }}>
              <Block sx={{ fontSize: 22, display: 'block' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                {t('pages.bookings.dialog.cancelTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('pages.bookings.dialog.cancelSubtitle')}
              </Typography>
            </Box>
          </Box>
          <IconButton
            edge="end"
            color="inherit"
            onClick={closeCancelDialog}
            aria-label="close"
            sx={{ color: 'text.secondary' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: '24px !important', pb: 3, bgcolor: '#ffffff' }}>
          <Stack spacing={3}>
            {selectedCancelBookingRow ? (
              <CancelBookingDetailsCard
                booking={selectedCancelBookingRow}
                t={t}
              />
            ) : (
              <FormControl fullWidth required size="small" error={Boolean(cancelErrors.booking)}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#718096', textTransform: 'uppercase', mb: 1, display: 'block' }}>
                  {t('pages.bookings.dialog.selectBooking')}
                </Typography>
                <Select
                  value={selectedCancelBooking}
                  disabled={dialogActionableBookingsLoading}
                  onChange={e => {
                    setSelectedCancelBooking(e.target.value);
                    setCancelErrors(err => ({ ...err, booking: '' }));
                  }}
                  sx={{
                    borderRadius: 2,
                    bgcolor: '#fff',
                    '& .MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 1.5 }
                  }}
                  renderValue={(selected) => {
                    const b = resolveCancelDialogBooking(null, selected, dialogActionableBookings);
                    if (!b) return t('pages.bookings.dialog.selectBooking');
                    return (
                      <>
                        <EventNote sx={{ fontSize: 18, color: '#d32f2f' }} />
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#1a202c' }}>
                          {getBookingCustomerDisplayName(b)} - {b.title}
                        </Typography>
                      </>
                    );
                  }}
                >
                  {dialogActionableBookings.map(b => (
                    <MenuItem key={b.id} value={b.id} sx={{ gap: 1.5, py: 1.5 }}>
                      <Box sx={{ p: 0.5, borderRadius: 1, bgcolor: 'rgba(211, 47, 47, 0.1)', color: '#d32f2f' }}>
                        <EventNote sx={{ fontSize: 18, display: 'block' }} />
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#1a202c' }}>
                        {getBookingCustomerDisplayName(b)} - {b.title}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
                {(cancelErrors.booking || dialogActionableBookingsLoading || dialogActionableBookings.length === 0) ? (
                  <FormHelperText error={Boolean(cancelErrors.booking)}>
                    {getActionableBookingsFieldHelperText(
                      cancelErrors.booking,
                      dialogActionableBookingsLoading,
                      t
                    )}
                  </FormHelperText>
                ) : null}
              </FormControl>
            )}

            {!selectedCancelBookingRow && selectedCancelBooking ? (
              <CancelBookingDetailsCard
                booking={resolveCancelDialogBooking(null, selectedCancelBooking, dialogActionableBookings)}
                t={t}
              />
            ) : null}

            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#718096', textTransform: 'uppercase', mb: 1, display: 'block' }}>
                {t('pages.bookings.dialog.cancelReason')}
              </Typography>
              <TextField
                fullWidth
                required
                multiline
                rows={3}
                value={cancelReason}
                onChange={e => {
                  setCancelReason(e.target.value);
                  setCancelErrors(err => ({ ...err, reason: '' }));
                }}
                error={Boolean(cancelErrors.reason)}
                helperText={cancelErrors.reason || ''}
                placeholder={t('pages.bookings.dialog.cancelReasonPlaceholder')}
                InputProps={{ sx: { borderRadius: 2, bgcolor: '#fff' } }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, bgcolor: '#fcf8f8', borderTop: '1px solid', borderColor: 'divider', gap: 1.5 }}>
          <Button onClick={closeCancelDialog} disabled={cancelSubmitting} sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}>
            {t('common.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleCancelBookingSubmit}
            disabled={cancelSubmitting}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              px: 4,
              py: 1,
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(211, 47, 47, 0.25)',
              '&:hover': {
                bgcolor: '#c62828',
                boxShadow: '0 6px 16px rgba(211, 47, 47, 0.35)',
              },
            }}
          >
            {cancelSubmitting ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : t('pages.bookings.dialog.cancelButton')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ textAlign: 'center' }}>{t('pages.bookings.dialog.updateStatusTitle')}</DialogTitle>
        <DialogContent dividers>
          <FormControl fullWidth required size="small" sx={{ mb: 2 }}>
            <InputLabel>{t('pages.bookings.dialog.statusLabel')}</InputLabel>
            <Select
              value={statusData.status}
              label={t('pages.bookings.dialog.statusLabel')}
              onChange={e => setStatusData(prev => ({ ...prev, status: e.target.value }))}
            >
              {statusUpdateOptions.map(opt => (
                <MenuItem key={opt} value={opt} sx={{ color: 'text.primary' }}>
                  {getStatusMeta(opt).label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={t('pages.bookings.dialog.notesLabel')}
            fullWidth
            multiline
            rows={3}
            value={statusData.notes}
            onChange={e => setStatusData(prev => ({ ...prev, notes: e.target.value }))}
            sx={{ mb: 1 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(statusData.notifyCustomer)}
                onChange={e => setStatusData(prev => ({ ...prev, notifyCustomer: e.target.checked }))}
              />
            }
            label={t('pages.bookings.dialog.notifyCustomer')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)} disabled={statusSubmitting}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleStatusSubmit} disabled={statusSubmitting}>
            {t('pages.bookings.dialog.updateStatusButton')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={transferDialogOpen}
        onClose={closeTransferDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          elevation: 8,
          sx: {
            borderRadius: isMobile ? 0 : 3,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 2.5,
            background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: 'rgba(255, 140, 66, 0.1)', color: '#FF8C42' }}>
              <SyncAlt sx={{ fontSize: 22, display: 'block' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                {t('pages.bookings.dialog.transferTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('pages.bookings.dialog.transferSubtitle')}
              </Typography>
            </Box>
          </Box>
          <IconButton
            edge="end"
            color="inherit"
            onClick={closeTransferDialog}
            aria-label="close"
            sx={{ color: 'text.secondary' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: '24px !important', pb: 3, bgcolor: '#ffffff' }}>
          <Stack spacing={3}>
            <FormControl fullWidth required size="small" error={Boolean(transferErrors.bookingId)}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#718096', textTransform: 'uppercase', mb: 1, display: 'block' }}>
                {t('pages.bookings.dialog.selectBookingFull')}
              </Typography>
              <Select
                value={transferData.bookingId}
                disabled={dialogActionableBookingsLoading}
                onChange={e => {
                  setTransferData(p => ({ ...p, bookingId: e.target.value }));
                  setTransferErrors(err => ({ ...err, bookingId: '' }));
                }}
                sx={{
                  borderRadius: 2,
                  bgcolor: '#fff',
                  '& .MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 1.5 }
                }}
                renderValue={(selected) => {
                  const b = dialogActionableBookings.find(x => String(x.id) === String(selected));
                  if (!b) return t('pages.bookings.dialog.selectBookingFull');
                  return (
                    <>
                      <EventNote sx={{ fontSize: 18, color: '#FF8C42' }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#1a202c' }}>
                        {[b.customerFirstName, b.customerLastName].filter(Boolean).join(' ') || b.customerName} - {b.title}
                      </Typography>
                    </>
                  );
                }}
              >
                {dialogActionableBookings.map(b => (
                  <MenuItem key={b.id} value={b.id} sx={{ gap: 1.5, py: 1.5 }}>
                    <Box sx={{ p: 0.5, borderRadius: 1, bgcolor: 'rgba(255, 140, 66, 0.1)', color: '#FF8C42' }}>
                      <EventNote sx={{ fontSize: 18, display: 'block' }} />
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#1a202c' }}>
                      {[b.customerFirstName, b.customerLastName].filter(Boolean).join(' ') || b.customerName} - {b.title}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
              {(transferErrors.bookingId || dialogActionableBookingsLoading || dialogActionableBookings.length === 0) ? (
                <FormHelperText error={Boolean(transferErrors.bookingId)}>
                  {getActionableBookingsFieldHelperText(
                    transferErrors.bookingId,
                    dialogActionableBookingsLoading,
                    t
                  )}
                </FormHelperText>
              ) : null}
            </FormControl>

            {/* Selected Booking Info Card (Dynamic) */}
            {transferData.bookingId && (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: '#f8fafc',
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  animation: 'fadeIn 0.3s ease-out',
                  '@keyframes fadeIn': { from: { opacity: 0, transform: 'translateY(-10px)' }, to: { opacity: 1, transform: 'translateY(0)' } }
                }}
              >
                <Box sx={{ p: 1, borderRadius: '50%', bgcolor: 'rgba(37, 99, 235, 0.1)', color: '#2563eb' }}>
                  <PeopleIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  {(() => {
                    const b = dialogActionableBookings.find(x => String(x.id) === String(transferData.bookingId));
                    if (!b) return null;
                    return (
                      <>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1a202c' }}>
                          {[b.customerFirstName, b.customerLastName].filter(Boolean).join(' ') || b.customerName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          {t('common.date')}: {formatRowSlotDate(b)}
                        </Typography>
                      </>
                    );
                  })()}
                </Box>
              </Box>
            )}

            <Divider>
              <Chip label="New Assignment" size="small" sx={{ fontSize: '0.65rem', fontWeight: 500, textTransform: 'uppercase', bgcolor: '#f1f5f9' }} />
            </Divider>

            {/* 2. Target Assignment */}
            <FormControl fullWidth required size="small" error={Boolean(transferErrors.newContactId)}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#718096', textTransform: 'uppercase', mb: 1, display: 'block' }}>
                {t('pages.bookings.dialog.toProvider')}
              </Typography>
              <Select
                value={transferData.newContactId}
                onChange={e => {
                  setTransferData(p => ({ ...p, newContactId: e.target.value, newSlotId: '' }));
                  setTransferErrors(err => ({ ...err, newContactId: '', newSlotId: '' }));
                }}
                sx={{
                  borderRadius: 2,
                  bgcolor: '#fff',
                  '& .MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 1.5 }
                }}
                renderValue={(selected) => {
                  const c = allContacts.find(x => String(x.id) === String(selected));
                  if (!c) return t('pages.bookings.dialog.toProvider');
                  return (
                    <>
                      <PeopleIcon sx={{ fontSize: 18, color: '#FF8C42' }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#1a202c' }}>{c.title}</Typography>
                    </>
                  );
                }}
              >
                {allContacts.map(c => (
                  <MenuItem key={c.id} value={c.id} sx={{ gap: 1.5, py: 1.5 }}>
                    <Box sx={{ p: 0.5, borderRadius: 1, bgcolor: 'rgba(255, 140, 66, 0.1)', color: '#FF8C42' }}>
                      <PeopleIcon sx={{ fontSize: 18, display: 'block' }} />
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#1a202c' }}>
                      {c.title} {c.contactPerson ? `(${c.contactPerson})` : ''}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
              {transferErrors.newContactId ? <FormHelperText>{transferErrors.newContactId}</FormHelperText> : null}
            </FormControl>

            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#718096', textTransform: 'uppercase', mb: 1, display: 'block' }}>
                {t('pages.bookings.filter.slotDateTitle')}
              </Typography>
              <DatePicker
                value={transferDate}
                onChange={val => {
                  setTransferDate(val);
                  setTransferData(p => ({ ...p, newSlotId: '' }));
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    sx: { '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }
                  }
                }}
              />
            </Box>

            <FormControl fullWidth required size="small" error={Boolean(transferErrors.newSlotId)}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#718096', textTransform: 'uppercase', mb: 1, display: 'block' }}>
                {t('pages.bookings.dialog.toSlot')}
              </Typography>
              <Select
                value={transferData.newSlotId}
                disabled={!transferData.newContactId || transferSlotsLoading}
                onChange={e => {
                  setTransferData(p => ({ ...p, newSlotId: e.target.value }));
                  setTransferErrors(err => ({ ...err, newSlotId: '' }));
                }}
                sx={{
                  borderRadius: 2,
                  bgcolor: '#fff',
                  '& .MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 1.5 }
                }}
                renderValue={(selected) => {
                  const s = transferSlots.find(x => String(x.id) === String(selected));
                  if (!s) return t('pages.bookings.dialog.toSlot');
                  return (
                    <>
                      <EventNote sx={{ fontSize: 18, color: '#FF8C42' }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#1a202c' }}>
                        {formatTransferSlotOptionLabel(s)}
                      </Typography>
                    </>
                  );
                }}
              >
                {transferSlotsLoading ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    {t('pages.bookings.loading')}
                  </MenuItem>
                ) : transferSlots.length === 0 ? (
                  <MenuItem disabled>{t('pages.bookings.dialog.noSlots') || 'No available slots'}</MenuItem>
                ) : (
                  transferSlots.map(s => {
                    const isAvail = isTransferSlotAvailable(s);
                    return (
                      <MenuItem
                        key={s.id}
                        value={s.id}
                        disabled={!isAvail}
                        sx={{ gap: 1.5, py: 1.5 }}
                      >
                        <Box
                          sx={{
                            p: 0.5,
                            borderRadius: 1,
                            bgcolor: isAvail ? 'rgba(255, 140, 66, 0.1)' : 'rgba(148, 163, 184, 0.12)',
                            color: isAvail ? '#FF8C42' : '#94a3b8',
                          }}
                        >
                          <EventNote sx={{ fontSize: 18, display: 'block' }} />
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: isAvail ? 500 : 400,
                            color: isAvail ? '#1a202c' : '#94a3b8',
                          }}
                        >
                          {formatTransferSlotOptionLabel(s)}
                        </Typography>
                      </MenuItem>
                    );
                  })
                )}
              </Select>
              {transferErrors.newSlotId ? <FormHelperText>{transferErrors.newSlotId}</FormHelperText> : null}
            </FormControl>

            {/* 3. Reason & Notification */}
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#718096', textTransform: 'uppercase', mb: 1, display: 'block' }}>
                {t('pages.bookings.dialog.transferReason')}
              </Typography>
              <TextField
                fullWidth
                required
                multiline
                rows={3}
                value={transferData.reason}
                onChange={e => {
                  setTransferData(p => ({ ...p, reason: e.target.value }));
                  setTransferErrors(err => ({ ...err, reason: '' }));
                }}
                error={Boolean(transferErrors.reason)}
                helperText={transferErrors.reason || ''}
                placeholder={t('pages.bookings.dialog.transferReasonPlaceholder')}
                InputProps={{ sx: { borderRadius: 2, bgcolor: '#fff' } }}
              />
            </Box>

            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f0f9ff', border: '1px solid #bae6fd' }}>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Switch
                    size="small"
                    checked={Boolean(transferData.notifyCustomer)}
                    onChange={e => setTransferData(p => ({ ...p, notifyCustomer: e.target.checked }))}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0369a1' }}>
                      {t('pages.bookings.dialog.notifyCustomer')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#0ea5e9' }}>
                      {t('pages.bookings.dialog.notifyCustomerHint')}
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, bgcolor: '#f8fafc', borderTop: '1px solid', borderColor: 'divider', gap: 1.5 }}>
          <Button onClick={closeTransferDialog} disabled={transferSubmitting} sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleTransferSubmit}
            disabled={transferSubmitting}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              px: 4,
              py: 1,
              borderRadius: 2,
              background: '#FF8C42',
              boxShadow: '0 4px 12px rgba(255, 140, 66, 0.25)',
              '&:hover': {
                background: '#E06526',
                boxShadow: '0 6px 16px rgba(255, 140, 66, 0.35)',
              },
            }}
          >
            {transferSubmitting ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : t('pages.bookings.dialog.transferButton')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create/Edit Booking Dialog */}
      <BookingForm
        open={openCreate || Boolean(editBooking)}
        booking={editBooking}
        onClose={() => { setOpenCreate(false); setEditBooking(null); }}
        onSuccess={() => {
          if (!editBooking) {
            toast.success(t('pages.bookings.toast.created'));
          } else {
            toast.success(t('pages.bookings.toast.updated'));
          }
          setOpenCreate(false);
          setEditBooking(null);
          fetchBookings();
        }}
      />

      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          elevation: 8,
          sx: {
            borderRadius: isMobile ? 0 : 3,
            overflow: 'hidden',
            border: isMobile ? 'none' : '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 2.5,
            background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {t('pages.bookings.dialog.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('pages.bookings.dialog.subtitle')}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 0, py: 0, bgcolor: '#f4f6f9' }}>
          {viewLoading ? (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={28} sx={{ color: '#FF8C42' }} />
              <Typography variant="body2" color="text.secondary">
                {t('pages.bookings.dialog.loading')}
              </Typography>
            </Box>
          ) : viewBookingData ? (
            <Stack spacing={0} divider={<Divider flexItem sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />}>
              <Box sx={{ px: 3, py: 2.5, bgcolor: '#fff' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box sx={{ p: 0.8, borderRadius: 1.5, bgcolor: 'rgba(255, 140, 66, 0.1)', color: '#FF8C42' }}>
                    <EditIcon sx={{ fontSize: 20, display: 'block' }} />
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a202c' }}>
                    {t('pages.bookings.dialog.sectionService')}
                  </Typography>
                </Box>
                <Grid container spacing={3} sx={{ mt: 0.5 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={detailLabelSx}>{t('pages.bookings.dialog.labelTitle')}</Typography>
                    <Typography sx={detailValueSx}>{viewBookingData.title || '—'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={detailLabelSx}>Meeting Type</Typography>
                    <Typography sx={{ ...detailValueSx, textTransform: 'capitalize' }}>{viewBookingData.meetingType || 'Offline'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={detailLabelSx}>{t('pages.bookings.dialog.labelContactPerson')}</Typography>
                    <Typography sx={detailValueSx}>
                      {viewBookingData.contactPerson || viewBookingData.providerName || '—'}
                    </Typography>
                  </Grid>
                  {viewBookingData.meetingUrl && (
                    <Grid item xs={12} sm={6}>
                      <Typography sx={detailLabelSx}>Meeting Link</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Button
                          variant="contained"
                          size="small"
                          href={viewBookingData.meetingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            textTransform: 'none',
                            borderRadius: 2,
                            bgcolor: '#FF8C42',
                            color: '#fff',
                            fontWeight: 700,
                            px: 2,
                            py: 0.5,
                            fontSize: '0.8rem',
                            boxShadow: '0 4px 12px rgba(255, 140, 66, 0.2)',
                            '&:hover': {
                              bgcolor: '#E06526',
                              boxShadow: '0 6px 16px rgba(255, 140, 66, 0.3)',
                            }
                          }}
                        >
                          Join Online Meeting
                        </Button>
                      </Box>
                    </Grid>
                  )}
                  {bookingHasSubcategoriesForDialog(viewBookingData) ? (
                    <Grid item xs={12}>
                      <Typography sx={detailLabelSx}>{t('pages.bookings.dialog.labelSubcategories')}</Typography>
                      <Typography sx={detailValueSx} component="div">
                        {viewSubcategoriesNamesLoading ? (
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={14} sx={{ color: '#FF8C42' }} />
                          </Box>
                        ) : (
                          formatBookingSubcategoriesForDialog(
                            viewBookingData,
                            viewBookingSubcategoryResolvedNames
                          ) || '—'
                        )}
                      </Typography>
                    </Grid>
                  ) : null}
                  <Grid item xs={12}>
                    <Typography sx={detailLabelSx}>{t('pages.bookings.dialog.labelAddress')}</Typography>
                    <Typography sx={detailValueSx}>{pickServiceAddress(viewBookingData) || '—'}</Typography>
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ px: 3, py: 2.5, bgcolor: '#fafbfc' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box sx={{ p: 0.8, borderRadius: 1.5, bgcolor: 'rgba(37, 99, 235, 0.1)', color: '#2563eb' }}>
                    <PeopleIcon sx={{ fontSize: 20, display: 'block' }} />
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a202c' }}>
                    {t('pages.bookings.dialog.sectionCustomer')}
                  </Typography>
                </Box>
                <Grid container spacing={3} sx={{ mt: 0.5 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={detailLabelSx}>{t('pages.bookings.dialog.labelName')}</Typography>
                    <Typography sx={detailValueSx}>
                      {[viewBookingData.customerFirstName, viewBookingData.customerLastName].filter(Boolean).join(' ') || viewBookingData.customerName || viewBookingData.name || '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={detailLabelSx}>{t('pages.bookings.dialog.labelEmail')}</Typography>
                    <Typography sx={detailValueSx}>{viewBookingData.customerEmail || viewBookingData.email || '—'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={detailLabelSx}>{t('pages.bookings.dialog.labelPhone')}</Typography>
                    <Typography sx={detailValueSx}>
                      {viewBookingData.customerPhone || viewBookingData.phone || viewBookingData.phoneNumber || '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={detailLabelSx}>{t('pages.bookings.dialog.labelGender')}</Typography>
                    <Typography sx={detailValueSx}>{viewBookingData.customerGender || viewBookingData.gender || '—'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={detailLabelSx}>{t('bookingForm.numberOfGuests')}</Typography>
                    <Typography sx={detailValueSx}>{viewBookingData.numberOfGuests || '—'}</Typography>
                  </Grid>

                </Grid>
              </Box>

              <Box sx={{ px: 3, py: 2.5, bgcolor: '#fff' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box sx={{ p: 0.8, borderRadius: 1.5, bgcolor: 'rgba(76, 175, 80, 0.1)', color: '#2e7d32' }}>
                    <EventNote sx={{ fontSize: 20, display: 'block' }} />
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a202c' }}>
                    {t('pages.bookings.dialog.schedule')}
                  </Typography>
                </Box>
                <Grid container spacing={3} sx={{ mt: 0.5 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={detailLabelSx}>{t('pages.bookings.dialog.confirmationCode')}</Typography>
                    <Typography
                      sx={{
                        ...detailValueSx,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                        letterSpacing: '0.06em',
                      }}
                    >
                      {pickBookingConfirmationCode(viewBookingData) || '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={detailLabelSx}>{t('pages.bookings.dialog.created')}</Typography>
                    <Typography sx={detailValueSx}>
                      {formatBookingDateTime(
                        viewBookingData.bookingCreatedAt || viewBookingData.createdAt || viewBookingData.requestedAt
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={detailLabelSx}>{t('pages.bookings.dialog.appointmentDate')}</Typography>
                    <Typography sx={detailValueSx}>
                      {formatAppointmentDateOnly(viewBookingData.appointmentDate || viewBookingData.date)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={detailLabelSx}>{t('pages.bookings.dialog.startTime')}</Typography>
                    <Typography sx={detailValueSx}>
                      {formatTimeField(viewBookingData.appointmentStartTime || viewBookingData.startTime)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={detailLabelSx}>{t('pages.bookings.dialog.endTime')}</Typography>
                    <Typography sx={detailValueSx}>
                      {formatTimeField(viewBookingData.appointmentEndTime || viewBookingData.endTime)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Stack>
          ) : (
            <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">{t('pages.bookings.dialog.noDetails')}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            bgcolor: '#fff',
            borderTop: '1px solid',
            borderColor: 'divider',
            justifyContent: isMobile ? 'center' : 'flex-end',
            gap: 1.5,
          }}
        >
          {viewBookingData && !(isCompletedBookingStatus(viewBookingData.status) || isConfirmedOrApprovedStatus(viewBookingData.status) || isCancelledBookingStatus(viewBookingData.status)) && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => {
                setViewDialogOpen(false);
                setEditBooking(viewBookingData);
              }}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                borderRadius: 2,
                color: '#FF8C42',
                borderColor: '#FF8C42',
                '&:hover': {
                  borderColor: '#E06526',
                  color: '#E06526',
                  bgcolor: 'rgba(255, 140, 66, 0.04)',
                },
              }}
            >
              {t('common.edit')}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={() => setViewDialogOpen(false)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              boxShadow: 'none',
              bgcolor: '#FF8C42',
              '&:hover': { bgcolor: '#E06526', boxShadow: '0 4px 12px rgba(255, 140, 66, 0.35)' },
            }}
          >
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={reminderDialogOpen}
        onClose={() => setReminderDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          elevation: 8,
          sx: {
            borderRadius: isMobile ? 0 : 3,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 2.5,
            background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: 'rgba(255, 140, 66, 0.1)', color: '#FF8C42' }}>
              <ReminderIcon sx={{ fontSize: 22, display: 'block' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                {t('pages.bookings.dialog.sendReminder')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('pages.bookings.dialog.remindCustomerSubtitle')}
              </Typography>
            </Box>
          </Box>
          <IconButton
            edge="end"
            color="inherit"
            onClick={() => setReminderDialogOpen(false)}
            aria-label="close"
            sx={{ color: 'text.secondary' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: '24px !important', pb: 3, bgcolor: '#ffffff' }}>
          <Stack spacing={3}>
            {/* Customer Info Card */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: '#f8fafc',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#718096', textTransform: 'uppercase', mb: 1, display: 'block' }}>
                {t('pages.bookings.dialog.sectionCustomer')}
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a202c' }}>
                {reminderData.bookingTitle || 'Booking'}
              </Typography>
              {(() => {
                const customerName = String(reminderData.customerName || '').trim();
                const slotDateDisplay = reminderData?.slotDateDisplay;
                const dateOk = slotDateDisplay != null && slotDateDisplay !== '—';
                const meta = [customerName || null, dateOk ? reminderData.slotDateDisplay : null].filter(Boolean);
                if (meta.length === 0) return null;
                return (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 500 }}>
                    {meta.join(' · ')}
                  </Typography>
                );
              })()}
            </Box>

            <FormControl fullWidth required>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#718096', textTransform: 'uppercase', mb: 1, display: 'block' }}>
                {t('pages.bookings.dialog.reminderType')}
              </Typography>
              <Select
                value={reminderData.type}
                onChange={e => setReminderData(prev => ({ ...prev, type: e.target.value }))}
                sx={{
                  borderRadius: 2,
                  '& .MuiSelect-select': { py: 1.5 },
                  bgcolor: '#fff'
                }}
              >
                <MenuItem value="email">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">Email</Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="sms">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">SMS</Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="both">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">Both Email & SMS</Typography>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#718096', textTransform: 'uppercase', mb: 1, display: 'block' }}>
                {t('pages.bookings.dialog.customMessage')}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={reminderData.customMessage}
                onChange={e => setReminderData(prev => ({ ...prev, customMessage: e.target.value }))}
                placeholder={t('pages.bookings.dialog.customMessagePlaceholder')}
                InputProps={{
                  sx: { borderRadius: 2, bgcolor: '#fff' }
                }}
              />
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            bgcolor: '#f8fafc',
            borderTop: '1px solid',
            borderColor: 'divider',
            gap: 1.5
          }}
        >
          <Button
            onClick={() => setReminderDialogOpen(false)}
            disabled={reminderSubmitting}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              color: 'text.secondary',
              px: 3
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleReminderSubmit}
            disabled={reminderSubmitting}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              px: 4,
              py: 1,
              bgcolor: '#FF8C42',
              boxShadow: '0 4px 12px rgba(255, 140, 66, 0.25)',
              '&:hover': {
                bgcolor: '#E06526',
                boxShadow: '0 6px 16px rgba(255, 140, 66, 0.35)',
              },
              '&.Mui-disabled': {
                bgcolor: 'action.disabledBackground',
              }
            }}
          >
            {reminderSubmitting ? (
              <CircularProgress size={24} sx={{ color: '#fff' }} />
            ) : (
              t('pages.bookings.dialog.sendReminder')
            )}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default Bookings;
